import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function FuelExpensesReport() {
  const [expenses, setExpenses] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.Expense.filter({ category: 'Fuel' }, '-date', 1000).then(e => { setExpenses(e); setLoading(false); });
  }, []);

  const filtered = expenses.filter(e => inRange(e.date, start, end));

  const totalSpend = filtered.reduce((s, e) => s + (e.amount || 0), 0);
  const totalGallons = filtered.reduce((s, e) => s + (parseFloat(e.gallons) || 0), 0);
  const avgPricePerGal = totalGallons > 0 ? totalSpend / totalGallons : 0;

  const csvRows = filtered.map(e => ({
    Date: e.date || '',
    Driver: e.driver_email || '',
    Vendor: e.vendor || '',
    State: e.fuel_state || '',
    Gallons: (parseFloat(e.gallons) || 0).toFixed(3),
    'Price/Gal': e.gallons ? (e.amount / e.gallons).toFixed(3) : '',
    Amount: (e.amount || 0).toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Date', 'Driver', 'Vendor', 'State', 'Gallons', 'Price/Gal', 'Amount'];
    const dataRows = filtered.map(e => [
      e.date || '', e.driver_email || '', e.vendor || '—', e.fuel_state || '—',
      (parseFloat(e.gallons) || 0).toFixed(3),
      e.gallons ? `$${(e.amount / e.gallons).toFixed(3)}` : '—',
      { value: `$${(e.amount || 0).toFixed(2)}`, class: 'negative' },
    ]);
    const totalsRow = ['TOTAL', '', '', '', totalGallons.toFixed(3), `$${avgPricePerGal.toFixed(3)} avg`, { value: `$${totalSpend.toFixed(2)}`, class: 'gold' }];
    const cards = [
      { label: 'Total Fuel Spend', value: `$${totalSpend.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'negative' },
      { label: 'Total Gallons', value: totalGallons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: '' },
      { label: 'Avg Price/Gal', value: `$${avgPricePerGal.toFixed(3)}`, color: 'gold' },
      { label: 'Fill-Ups', value: filtered.length, color: '' },
    ];
    const html = generatePrintHTML('Fuel Expenses Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'fuel-expenses.csv')} exportDisabled={loading || filtered.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Fuel Spend', value: `$${totalSpend.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'text-red-400' },
          { label: 'Total Gallons', value: totalGallons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-foreground' },
          { label: 'Avg Price/Gal', value: `$${avgPricePerGal.toFixed(3)}`, color: 'text-primary' },
          { label: 'Fill-Ups', value: filtered.length, color: 'text-foreground' },
        ].map(i => (
          <div key={i.label} className="glass-card p-4 text-center">
            <p className={`text-lg font-space font-bold ${i.color}`}>{i.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{i.label}</p>
          </div>
        ))}
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Date', 'Driver', 'Vendor', 'State', 'Gallons', 'Price/Gal', 'Amount'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(e => (
              <tr key={e.id} className="hover:bg-white/3">
                <td className="px-3 py-2.5 text-muted-foreground">{e.date}</td>
                <td className="px-3 py-2.5 text-foreground">{e.driver_email}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.vendor || '—'}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.fuel_state || '—'}</td>
                <td className="px-3 py-2.5 text-foreground">{(parseFloat(e.gallons) || 0).toFixed(3)}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.gallons ? `$${(e.amount / e.gallons).toFixed(3)}` : '—'}</td>
                <td className="px-3 py-2.5 font-semibold text-foreground">${(e.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={4} className="px-3 py-2.5 text-foreground">TOTAL</td>
                <td className="px-3 py-2.5 text-foreground">{totalGallons.toFixed(3)}</td>
                <td className="px-3 py-2.5 text-foreground">${avgPricePerGal.toFixed(3)} avg</td>
                <td className="px-3 py-2.5 text-foreground">${totalSpend.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No fuel expenses found.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
